import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Link, useNavigate } from 'react-router-dom';
import { useLocation } from '../../../contexts/LocationContext';
import { useBusinessTypeContext } from '../../../contexts/BusinessTypeContext';
import { useMutation } from '../../../hooks/useApi';
import LoadingSpinner from '../../../components/UI/LoadingSpinner';
import apiClient, { getErrorMessage } from '../../../utils/api';
import { GoatMaster } from '../../../types/api';

interface GoatBreedingFormData {
  doe_tag: string;
  buck_tag: string;
  breeding_date: string;
  pregnancy_check?: boolean;
  expected_kidding?: string;
}

const breedingSchema = yup.object({
  doe_tag: yup.string().required('Doe tag is required'),
  buck_tag: yup.string().required('Buck tag is required'),
  breeding_date: yup.string().required('Breeding date is required'),
  pregnancy_check: yup.boolean().optional(),
  expected_kidding: yup.string().optional(),
});

const GoatBreedingForm: React.FC = () => {
  const navigate = useNavigate();
  const { selectedLocation } = useLocation();
  const { businessType } = useBusinessTypeContext();
  const [error, setError] = useState('');
  const [does, setDoes] = useState<GoatMaster[]>([]);
  const [bucks, setBucks] = useState<GoatMaster[]>([]);
  const [loadingGoats, setLoadingGoats] = useState(true);

  const { mutate: createBreedingLog, loading: creating } = useMutation<any, GoatBreedingFormData>();

  const form = useForm<GoatBreedingFormData>({
    resolver: yupResolver(breedingSchema) as any,
    defaultValues: {
      doe_tag: '',
      buck_tag: '',
      breeding_date: new Date().toISOString().split('T')[0],
      expected_kidding: '',
    },
  });

  useEffect(() => {
    if (businessType === 'goat' && selectedLocation?.id) {
      loadGoats();
    }
  }, [selectedLocation?.id, businessType]);

  const loadGoats = async () => {
    try {
      setLoadingGoats(true);
      const response = await apiClient.get(`/goat/master?location_id=${selectedLocation?.id}`);
      const allGoats = response.data || [];
      setDoes(allGoats.filter((g: GoatMaster) => g.category === 'doe' && g.status === 'active'));
      // Only show bucks that are marked as breeders
      setBucks(allGoats.filter((g: GoatMaster) => 
        g.category === 'buck' && 
        g.status === 'active' && 
        g.is_breeder === true
      ));
    } catch (err) {
      console.error('Failed to load goats:', err);
    } finally {
      setLoadingGoats(false);
    }
  };

  const handleSubmit = async (data: GoatBreedingFormData) => {
    try {
      setError('');
      const breedingData = {
        ...data,
        location_id: selectedLocation?.id,
        pregnancy_check: false, // Always false on creation - can be updated later via listing buttons
      };
      await createBreedingLog('/goat/breeding-logs', breedingData);
      navigate('/goat/breeding-logs');
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to create breeding record'));
    }
  };

  // Calculate expected kidding date (approximately 150 days after breeding)
  const calculateExpectedKidding = (breedingDate: string) => {
    if (!breedingDate) return '';
    const date = new Date(breedingDate);
    date.setDate(date.getDate() + 150); // Average gestation period
    return date.toISOString().split('T')[0];
  };

  const watchedBreedingDate = form.watch('breeding_date');

  // Auto-calculate expected kidding date whenever breeding date changes
  useEffect(() => {
    if (watchedBreedingDate) {
      const expected = calculateExpectedKidding(watchedBreedingDate);
      form.setValue('expected_kidding', expected);
    } else {
      form.setValue('expected_kidding', '');
    }
  }, [watchedBreedingDate]);

  if (businessType !== 'goat') {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600">This form is only available for goat farming locations.</p>
        <Link to="/dashboard" className="text-blue-600 hover:text-blue-800">Go to Dashboard</Link>
      </div>
    );
  }

  if (!selectedLocation) {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600">Please select a location first</p>
        <Link to="/locations" className="text-blue-600 hover:text-blue-800">Go to Location Selection</Link>
      </div>
    );
  }

  return (
    <div className="container-mobile py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Link to="/goat/breeding-logs" className="btn-touch p-2 text-gray-400 hover:text-gray-600">
          <ArrowLeftIcon className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">💑 Record Breeding</h1>
          <p className="text-gray-600">{selectedLocation.name}</p>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Form */}
      <div className="card p-6">
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="form-label">Doe Tag *</label>
              {loadingGoats ? (
                <div className="form-input bg-gray-50 flex items-center">
                  <LoadingSpinner size="sm" />
                  <span className="ml-2 text-sm text-gray-500">Loading does...</span>
                </div>
              ) : (
                <select {...form.register('doe_tag')} className="form-input">
                  <option value="">Select Doe</option>
                  {does.map((doe) => (
                    <option key={doe.id} value={doe.tag_no}>
                      {doe.tag_no} {doe.breed ? `(${doe.breed})` : ''}
                    </option>
                  ))}
                </select>
              )}
              {form.formState.errors.doe_tag && (
                <p className="mt-1 text-sm text-red-600">{form.formState.errors.doe_tag.message}</p>
              )}
            </div>

            <div>
              <label className="form-label">Buck Tag *</label>
              {loadingGoats ? (
                <div className="form-input bg-gray-50 flex items-center">
                  <LoadingSpinner size="sm" />
                  <span className="ml-2 text-sm text-gray-500">Loading bucks...</span>
                </div>
              ) : (
                <select {...form.register('buck_tag')} className="form-input">
                  <option value="">Select Buck</option>
                  {bucks.map((buck) => (
                    <option key={buck.id} value={buck.tag_no}>
                      {buck.tag_no} {buck.breed ? `(${buck.breed})` : ''}
                    </option>
                  ))}
                </select>
              )}
              {form.formState.errors.buck_tag && (
                <p className="mt-1 text-sm text-red-600">{form.formState.errors.buck_tag.message}</p>
              )}
            </div>

            <div>
              <label className="form-label">Breeding Date *</label>
              <input
                type="date"
                {...form.register('breeding_date')}
                className="form-input"
              />
              {form.formState.errors.breeding_date && (
                <p className="mt-1 text-sm text-red-600">{form.formState.errors.breeding_date.message}</p>
              )}
            </div>

            <div>
              <label className="form-label">Expected Kidding Date</label>
              <input
                type="date"
                {...form.register('expected_kidding')}
                className="form-input bg-gray-50 cursor-not-allowed"
                readOnly
              />
              <p className="mt-1 text-xs text-gray-500">Auto-calculated (150 days after breeding)</p>
            </div>
          </div>

          <div className="flex space-x-4 pt-4">
            <button
              type="submit"
              disabled={creating}
              className="flex-1 btn-touch bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center"
            >
              {creating ? <LoadingSpinner size="sm" /> : 'Record Breeding'}
            </button>
            <Link
              to="/goat/breeding-logs"
              className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GoatBreedingForm;
